import React from 'react'
import {
  dropTimeZoneFromDate,
  addMinutesToDate,
} from '../../../../shared/libraries/utils/date'
import { MEETING_POINT_STATUS } from './options'

const getPolygonCenter = boundary => {
  if (!boundary || boundary.length === 0) return null
  const lat = boundary.reduce((sum, p) => sum + p.latitude, 0) / boundary.length
  const lng =
    boundary.reduce((sum, p) => sum + p.longitude, 0) / boundary.length
  return { lat, lng }
}

const getMissionsCenter = missions => {
  if (!missions || missions.length === 0) return null
  const centers = missions
    .map(m => getPolygonCenter(m.boundary))
    .filter(Boolean)
  if (centers.length === 0) return null
  const lat = centers.reduce((sum, c) => sum + c.lat, 0) / centers.length
  const lng = centers.reduce((sum, c) => sum + c.lng, 0) / centers.length
  return { lat, lng }
}

const formatPolygons = (data = []) =>
  data.map(mission => ({
    path: (mission.boundary || []).map(point => ({
      lat: point.latitude,
      lng: point.longitude,
    })),
    options: {
      fillColor: mission.boundaryColour,
      fillOpacity: 0.3,
      strokeColor: mission.boundaryColour,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      clickable: false,
      draggable: false,
      editable: false,
      zIndex: 1,
    },
  }))

const getMeetingPointStatus = missionsToEdit => {
  if (missionsToEdit.length === 1) {
    if (missionsToEdit[0].meetingPoint) {
      return MEETING_POINT_STATUS.SINGLE_EDIT
    } else {
      return MEETING_POINT_STATUS.SINGLE_ADD
    }
  }

  const allEmpty = missionsToEdit.every(m => !m.meetingPoint)
  if (allEmpty) return MEETING_POINT_STATUS.MULTIPLE_ADD

  const allHave = missionsToEdit.every(m => !!m.meetingPoint)
  if (allHave) {
    const firstMeetingPoint = JSON.stringify(missionsToEdit[0].meetingPoint)
    const allSame = missionsToEdit.every(
      m => JSON.stringify(m.meetingPoint) === firstMeetingPoint,
    )
    if (allSame) return MEETING_POINT_STATUS.MULTIPLE_EDIT
  }

  const atLeastOne = missionsToEdit.some(m => !!m.meetingPoint)
  if (atLeastOne) return MEETING_POINT_STATUS.MULTIPLE_SET
}

const getMaxDate = missions => {
  const missionsDateTimes = missions.map(mission =>
    dropTimeZoneFromDate(mission.dateTime),
  )
  const maxDateStr = missionsDateTimes.reduce(
    (max, curr) => (curr > max ? curr : max),
    missionsDateTimes[0],
  )
  const maxDate = new Date(maxDateStr)
  const pad = n => n.toString().padStart(2, '0')
  const formatted = `${maxDate.getFullYear()}-${pad(
    maxDate.getMonth() + 1,
  )}-${pad(maxDate.getDate())}T${pad(maxDate.getHours())}:${pad(
    maxDate.getMinutes(),
  )}:${pad(maxDate.getSeconds())}`

  return formatted
}

const getMinDate = missions => {
  const missionsDateTimes = missions.map(mission =>
    dropTimeZoneFromDate(mission.dateTime),
  )

  const minDateStr = missionsDateTimes.reduce(
    (min, curr) => (curr < min ? curr : min),
    missionsDateTimes[0],
  )

  const minDate = new Date(minDateStr)
  //-180 minutes = -3 hours
  const newDate = addMinutesToDate(minDate, -180)
  const pad = n => n.toString().padStart(2, '0')
  const formatted = `${newDate.getFullYear()}-${pad(
    newDate.getMonth() + 1,
  )}-${pad(newDate.getDate())}T${pad(newDate.getHours())}:${pad(
    newDate.getMinutes(),
  )}:${pad(newDate.getSeconds())}`

  return formatted
}

const getDefaultDate = (missions, status) => {
  const missionsDateTimes = missions.map(mission =>
    dropTimeZoneFromDate(mission.dateTime),
  )
  if (
    [
      MEETING_POINT_STATUS.MULTIPLE_SET,
      MEETING_POINT_STATUS.MULTIPLE_ADD,
      MEETING_POINT_STATUS.SINGLE_ADD,
    ].includes(status)
  ) {
    const minDateStr = missionsDateTimes.reduce(
      (min, curr) => (curr < min ? curr : min),
      missionsDateTimes[0],
    )
    const minDate = new Date(minDateStr)
    const newDate = addMinutesToDate(minDate, -15)
    const pad = n => n.toString().padStart(2, '0')
    const formatted = `${newDate.getFullYear()}-${pad(
      newDate.getMonth() + 1,
    )}-${pad(newDate.getDate())}T${pad(newDate.getHours())}:${pad(
      newDate.getMinutes(),
    )}:${pad(newDate.getSeconds())}`

    return formatted
  } else if (
    [
      MEETING_POINT_STATUS.SINGLE_EDIT,
      MEETING_POINT_STATUS.MULTIPLE_EDIT,
    ].includes(status)
  ) {
    return missions[0].meetingPoint.dateTime
  }
}

const getDefaultCenter = (missionsToEdit, status) => {
  if (
    [
      MEETING_POINT_STATUS.MULTIPLE_SET,
      MEETING_POINT_STATUS.MULTIPLE_ADD,
      MEETING_POINT_STATUS.SINGLE_ADD,
    ].includes(status)
  ) {
    return getMissionsCenter(missionsToEdit)
  } else if (
    [MEETING_POINT_STATUS.SINGLE_EDIT, MEETING_POINT_STATUS.MULTIPLE_EDIT]
  ) {
    const meetingPoint = missionsToEdit[0].meetingPoint
    return { lat: meetingPoint.latitude, lng: meetingPoint.longitude }
  }
}
const getHint = (status, classes) => {
  if (
    [
      MEETING_POINT_STATUS.SINGLE_ADD,
      MEETING_POINT_STATUS.SINGLE_EDIT,
    ].includes(status)
  ) {
    return null
  } else if (
    [
      MEETING_POINT_STATUS.MULTIPLE_ADD,
      MEETING_POINT_STATUS.MULTIPLE_EDIT,
    ].includes(status)
  ) {
    return (
      <div>
        Editing common meeting point for selected missions. All selected
        missions currently share these details.
      </div>
    )
  } else if ([MEETING_POINT_STATUS.MULTIPLE_SET].includes(status)) {
    return (
      <div className={classes.warningText}>
        Mixed/No existing meeting points. Default values shown. Saving will
        apply as a new common meeting point to all selected missions (overriding
        existing).
      </div>
    )
  }
}

const getTitle = status => {
  if (
    [
      MEETING_POINT_STATUS.SINGLE_ADD,
      MEETING_POINT_STATUS.MULTIPLE_ADD,
    ].includes(status)
  ) {
    return 'Add Meeting point'
  } else if (
    [
      MEETING_POINT_STATUS.MULTIPLE_EDIT,
      MEETING_POINT_STATUS.SINGLE_EDIT,
    ].includes(status)
  ) {
    return 'Edit Meeting point'
  } else if ([MEETING_POINT_STATUS.MULTIPLE_SET].includes(status)) {
    return 'Set Meeting point'
  }
}

const handleDateTimeError = ({
  dateTime,
  minDate,
  maxDate,
  status,
  setDateError,
}) => {
  if (new Date(dateTime) < new Date(minDate)) {
    if (
      [
        MEETING_POINT_STATUS.SINGLE_ADD,
        MEETING_POINT_STATUS.SINGLE_EDIT,
      ].includes(status)
    ) {
      setDateError('The date cannot be earlier than 3 hours before the mission')
    } else if (
      [
        MEETING_POINT_STATUS.MULTIPLE_ADD,
        MEETING_POINT_STATUS.MULTIPLE_EDIT,
        MEETING_POINT_STATUS.MULTIPLE_SET,
      ].includes(status)
    ) {
      setDateError(
        'The date cannot be earlier than 3 hours before the first mission',
      )
    }
  } else if (new Date(dateTime) > new Date(maxDate)) {
    if (
      [
        MEETING_POINT_STATUS.SINGLE_ADD,
        MEETING_POINT_STATUS.SINGLE_EDIT,
      ].includes(status)
    ) {
      setDateError('Date cannot be after mission date')
    } else if (
      [
        MEETING_POINT_STATUS.MULTIPLE_ADD,
        MEETING_POINT_STATUS.MULTIPLE_EDIT,
        MEETING_POINT_STATUS.MULTIPLE_SET,
      ].includes(status)
    ) {
      setDateError('Date cannot be after date last mission')
    }
  } else {
    setDateError(false)
  }
}

const formatAddress = data => {
  // const { streetNumber, route } = data.reduce(
  //   (acc, item) => {
  //     if (item.types.length === 1) {
  //       if (item.types[0] === 'street_number') {
  //         acc.streetNumber = item.short_name
  //       }

  //       if (item.types[0] === 'route') {
  //         acc.route = item.short_name
  //       }
  //     }

  //     return acc
  //   },
  //   { streetNumber: '', route: '' },
  // )

  // if (!streetNumber && !route) {
  //   return 'Enter address manually'
  // }

  // const address = `${streetNumber} ${route}`.trim()

  // return address

  const address = data.reduce((acc, item, i, arr) => {
    const skipTypes = [
      'postal_code',
      'country',
      'administrative_area_level_1',
      'administrative_area_level_2',
      'postal_code_suffix',
    ]

    if (item.types && !item.types.some(type => skipTypes.includes(type))) {
      acc += `${item.short_name}`

      const isOnlyRoute = item.types.length === 1 && item.types[0] === 'route'
      const isNotLast = i < arr.length - 1

      if (isOnlyRoute && isNotLast) {
        acc += ','
      }

      acc += ' '
    }

    return acc
  }, '')

  return address.trim()
}

export {
  getPolygonCenter,
  getMissionsCenter,
  formatPolygons,
  getMinDate,
  getDefaultDate,
  getMeetingPointStatus,
  getDefaultCenter,
  getHint,
  getTitle,
  getMaxDate,
  handleDateTimeError,
  formatAddress,
}
